import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import * as DocumentPicker from 'expo-document-picker';
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import type {
  CsvAttachment,
  ImportCustomersData,
  ImportCustomersErrorBody,
} from '@/api/partners/organizations/use-import-customers';
import {
  normalizeImportCustomersData,
  parseImportCustomersErrorData,
  useImportCustomers,
} from '@/api/partners/organizations/use-import-customers';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Icon } from '@/ui/icon';
import { Checkmark } from '@/ui/icons/checkmark';
import { ChevronDown } from '@/ui/icons/chevron-down';
import { ChevronUp } from '@/ui/icons/chevron-up';
import { CsvUpload } from '@/ui/icons/csv-upload';
import { DownloadCircle } from '@/ui/icons/download-circle';
import { Warning } from '@/ui/icons/warning';
import { Loader } from '@/ui/loader';

import {
  buildFallbackErrorReportCsv,
  CUSTOMER_IMPORT_FOOTER,
  downloadCustomerImportTemplate,
  downloadErrorReportCsv,
} from './constants';

const FILE_IMPORT_ERROR_TITLE = "Couldn't Import This File";
const FILE_IMPORT_ERROR_BODY =
  'Make sure your file is a CSV with the exact required columns.';

type ImportStep = 'idle' | 'uploading' | 'success' | 'review' | 'error';

type Props = {
  isVisible: boolean;
  onClose: () => void;
};

const normalizeDocumentAsset = async (
  asset: DocumentPicker.DocumentPickerAsset
): Promise<CsvAttachment> => {
  if (!asset.name) {
    throw new Error('CSV upload requires a file name.');
  }

  const response = await fetch(asset.uri);
  const blob = await response.blob();
  const file = new File([blob], asset.name, {
    type: asset.mimeType || 'text/csv',
  });

  return {
    name: asset.name,
    file,
  };
};

const normalizeDroppedFile = (file: File): CsvAttachment => ({
  name: file.name,
  file,
});

const getSuccessSubtitle = (data: ImportCustomersData) => {
  if (data.importedCount > 0) {
    return data.importedCount === 1
      ? '1 customer added'
      : `All ${data.importedCount} customers added`;
  }

  if (data.skippedCount > 0) {
    return `${data.skippedCount} customers were already in your list`;
  }

  return 'Import complete';
};

const getFailedCount = (data: ImportCustomersData) =>
  data.failedCount || data.validationErrors.length;

const hasImportErrors = (data: ImportCustomersData) =>
  data.errorReportEmailed || getFailedCount(data) > 0;

const getReviewSuccessSubtitle = (data: ImportCustomersData) => {
  const failedCount = getFailedCount(data);

  return [
    `${data.importedCount} added`,
    `${failedCount} need fixing`,
    `${data.skippedCount} duplicates`,
  ].join(' · ');
};

const getErrorReportCsvContent = (data: ImportCustomersData) =>
  data.errorReportCsv ?? buildFallbackErrorReportCsv(data.validationErrors);

const canDownloadErrorReport = (data: ImportCustomersData) =>
  getFailedCount(data) > 0 && getErrorReportCsvContent(data) !== null;

const handleDownloadErrorReport = (
  data: ImportCustomersData,
  sourceFilename?: string | null
) => {
  const csv = getErrorReportCsvContent(data);

  if (csv) {
    downloadErrorReportCsv(csv, sourceFilename);
  }
};

const isTotalImportFailure = (data: ImportCustomersData) =>
  data.importedCount === 0 && getFailedCount(data) > 0;

const getErrorReportEmailMessage = (email?: string) => {
  if (email) {
    return `Download for the full list. We've also emailed this report to ${email}`;
  }

  return `Download for the full list. We've also emailed this report to your account email.`;
};

export const ImportCustomerModal = ({ isVisible, onClose }: Props) => {
  const { styles, theme } = useStyles(stylesheet);
  const queryClient = useQueryClient();

  const [step, setStep] = useState<ImportStep>('idle');
  const [file, setFile] = useState<CsvAttachment | null>(null);
  const [result, setResult] = useState<ImportCustomersData | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [needsFixingExpanded, setNeedsFixingExpanded] = useState(false);
  const [priorRentersExpanded, setPriorRentersExpanded] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const dropZoneRef = useRef<View>(null);
  const stepRef = useRef(step);
  stepRef.current = step;
  const uploadFileRef = useRef<(attachment: CsvAttachment) => Promise<void>>(
    async () => {}
  );

  const { mutateAsync: importCustomers } = useImportCustomers();
  const { data: customer } = useCustomer();

  const resetState = () => {
    setStep('idle');
    setFile(null);
    setResult(null);
    setFileError(null);
    setNeedsFixingExpanded(false);
    setPriorRentersExpanded(false);
    setIsDragActive(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleImportSuccess = (data: ImportCustomersData) => {
    const normalizedData = normalizeImportCustomersData(data);
    setResult(normalizedData);
    setFileError(null);

    if (normalizedData.importedCount > 0) {
      queryClient.invalidateQueries({
        queryKey: ['internal/partners/organizations/customers'],
      });
    }

    if (
      normalizedData.priorRenters.length > 0 ||
      hasImportErrors(normalizedData)
    ) {
      setStep('review');
      return;
    }

    setStep('success');
  };

  const handleImportError = (err: unknown) => {
    const axiosErr = err as AxiosError<ImportCustomersErrorBody>;
    const responseData = axiosErr.response?.data;
    const parsedData = parseImportCustomersErrorData(responseData?.data);

    if (parsedData) {
      setResult(parsedData);
      setFileError(responseData?.errors?.file ?? null);

      if (parsedData.importedCount > 0) {
        queryClient.invalidateQueries({
          queryKey: ['internal/partners/organizations/customers'],
        });
      }

      setStep('review');
      return;
    }

    setFileError(responseData?.errors?.file ?? null);
    setStep('error');
  };

  const resetToUpload = () => {
    setStep('idle');
    setFile(null);
    setFileError(null);
    setResult(null);
    setNeedsFixingExpanded(false);
    setPriorRentersExpanded(false);
  };

  const uploadFile = async (attachment: CsvAttachment) => {
    setFile(attachment);
    setFileError(null);
    setResult(null);
    setStep('uploading');

    try {
      const response = await importCustomers({ file: attachment });
      handleImportSuccess(response.data);
    } catch (err) {
      handleImportError(err);
    }
  };
  uploadFileRef.current = uploadFile;

  const pickCsvFile = async () => {
    if (step === 'uploading') {
      return;
    }

    try {
      const pickerResult = await DocumentPicker.getDocumentAsync({
        type: ['text/csv', 'text/comma-separated-values', 'application/csv'],
        copyToCacheDirectory: false,
        multiple: false,
      });

      if (pickerResult.canceled) {
        return;
      }

      const attachment = await normalizeDocumentAsset(pickerResult.assets[0]);

      await uploadFile(attachment);
    } catch (error) {
      setFileError(
        error instanceof Error ? error.message : 'Failed to read CSV file.'
      );
      setStep('error');
    }
  };

  const showsDropZone =
    step === 'idle' || step === 'uploading' || step === 'error';

  useEffect(() => {
    if (!isVisible || !showsDropZone) {
      return;
    }

    const element = dropZoneRef.current as unknown as HTMLElement | null;

    if (!element) {
      return;
    }

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }

      setIsDragActive(true);
    };

    const handleDragLeave = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!element.contains(event.relatedTarget as Node)) {
        setIsDragActive(false);
      }
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragActive(false);

      if (stepRef.current === 'uploading') {
        return;
      }

      const droppedFile = event.dataTransfer?.files?.[0];

      if (!droppedFile) {
        return;
      }

      uploadFileRef.current(normalizeDroppedFile(droppedFile));
    };

    element.addEventListener('dragover', handleDragOver, true);
    element.addEventListener('dragleave', handleDragLeave, true);
    element.addEventListener('drop', handleDrop, true);

    return () => {
      element.removeEventListener('dragover', handleDragOver, true);
      element.removeEventListener('dragleave', handleDragLeave, true);
      element.removeEventListener('drop', handleDrop, true);
    };
  }, [isVisible, showsDropZone]);

  const renderDropZone = () => (
    <View
      ref={dropZoneRef}
      style={[styles.dropZone, isDragActive ? styles.dropZoneActive : null]}
    >
      <Pressable
        onPress={pickCsvFile}
        disabled={step === 'uploading'}
        style={styles.dropZonePressable}
      >
        {step === 'uploading' ? (
          <Loader style={{ height: 120, width: 120 }} />
        ) : (
          <>
            <Icon
              type="naked"
              icon={<CsvUpload />}
              fillColor={theme.colors.icon.color}
              width={18}
              height={22}
            />

            <Text type="bodyRegular" style={{ textAlign: 'center' }}>
              Drag and Drop your CSV Here
            </Text>

            <Text
              type="caption1"
              style={{
                color: theme.colors.text.secondary,
                textAlign: 'center',
              }}
            >
              Or click to browse
            </Text>
          </>
        )}
      </Pressable>
    </View>
  );

  const renderTemplateCard = () => (
    <Pressable
      onPress={downloadCustomerImportTemplate}
      style={styles.templateCard}
    >
      <View style={{ gap: 4, flex: 1 }}>
        <Text type="title2">Download CSV Template</Text>

        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          Start here
        </Text>
      </View>

      <View style={styles.templateDownloadIcon}>
        <Icon
          type="naked"
          icon={<DownloadCircle />}
          fillColor={theme.colors.icon.color}
          width={22}
          height={22}
        />
      </View>
    </Pressable>
  );

  const renderSuccessPanel = (title: string, subtitle: string) => (
    <View style={styles.successPanel}>
      <View style={styles.successIconCircle}>
        <Icon
          type="naked"
          icon={<Checkmark />}
          fillColor={theme.colors.success}
          width={12}
          height={12}
        />
      </View>

      <Text type="bodyRegular" style={{ textAlign: 'center' }}>
        {title}
      </Text>

      <Text
        type="bodyRegular"
        style={{ color: theme.colors.text.secondary, textAlign: 'center' }}
      >
        {subtitle}
      </Text>
    </View>
  );

  const renderReviewSummaryPanel = (
    title: string,
    subtitle: string,
    variant: 'warning' | 'success'
  ) => (
    <View
      style={
        variant === 'warning'
          ? styles.reviewPanelWarning
          : styles.reviewPanelSuccess
      }
    >
      {variant === 'success' ? (
        <View style={styles.reviewSummaryIconCircle}>
          <Icon
            type="naked"
            icon={<Checkmark />}
            fillColor={theme.colors.success}
            width={12}
            height={12}
          />
        </View>
      ) : (
        <Icon
          type="naked"
          icon={<Warning />}
          fillColor={theme.colors.icon.errorColor}
          width={20}
          height={20}
        />
      )}

      <Text type="bodyRegular" style={{ textAlign: 'center' }}>
        {title}
      </Text>

      <Text
        type="bodyRegular"
        style={{ color: theme.colors.text.secondary, textAlign: 'center' }}
      >
        {subtitle}
      </Text>
    </View>
  );

  const renderNeedsFixingSection = () => {
    if (!result || !hasImportErrors(result)) {
      return null;
    }

    const failedCount = getFailedCount(result);

    return (
      <View style={styles.reviewRow}>
        <Pressable
          onPress={() => setNeedsFixingExpanded((prev) => !prev)}
          style={styles.reviewRowHeader}
        >
          <Icon
            type="naked"
            icon={<Warning />}
            fillColor={theme.colors.icon.errorColor}
            width={16}
            height={16}
          />

          <Text type="bodyRegular" style={{ flex: 1 }}>
            Needs Fixing{failedCount > 0 ? ` (${failedCount})` : ''}
          </Text>

          <Icon
            type="naked"
            icon={needsFixingExpanded ? <ChevronUp /> : <ChevronDown />}
            fillColor={theme.colors.icon.color}
            width={12}
            height={12}
          />
        </Pressable>

        {needsFixingExpanded ? (
          <View style={styles.needsFixingExpandedContent}>
            <View style={styles.needsFixingContainer}>
              {result.validationErrors.length > 0 ? (
                <ScrollView
                  style={styles.errorList}
                  nestedScrollEnabled
                  showsVerticalScrollIndicator
                >
                  {result.validationErrors.map((error, index) => (
                    <View
                      key={`${error.row}-${error.message}-${index}`}
                      style={styles.errorRow}
                    >
                      <View style={styles.errorRowBadge}>
                        <Text type="caption1">Row {error.row}</Text>
                      </View>

                      <Text
                        type="caption1"
                        style={{
                          color: theme.colors.text.secondary,
                          flex: 1,
                        }}
                      >
                        {error.message}
                      </Text>
                    </View>
                  ))}
                </ScrollView>
              ) : (
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Some rows could not be imported. Check your email for the full
                  error report.
                </Text>
              )}
            </View>

            {canDownloadErrorReport(result) || result.errorReportEmailed ? (
              <View style={styles.errorReportSection}>
                {canDownloadErrorReport(result) ? (
                  <Pressable
                    onPress={() =>
                      handleDownloadErrorReport(result, file?.name)
                    }
                  >
                    <Text type="bodyBold" style={styles.errorReportLink}>
                      Download Error Report (CSV)
                    </Text>
                  </Pressable>
                ) : null}

                {result.errorReportEmailed ? (
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {getErrorReportEmailMessage(customer?.email)}
                  </Text>
                ) : null}
              </View>
            ) : null}
          </View>
        ) : null}
      </View>
    );
  };

  const renderReviewSections = () => {
    if (!result) {
      return null;
    }

    const reviewTitle = `Import of ${file?.name ?? 'customers.csv'}`;
    const reviewSubtitle = getReviewSuccessSubtitle(result);

    return (
      <View style={{ gap: 8 }}>
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          Review Before Importing
        </Text>

        {hasImportErrors(result) || isTotalImportFailure(result)
          ? renderReviewSummaryPanel(
              reviewTitle,
              fileError ?? reviewSubtitle,
              'warning'
            )
          : renderReviewSummaryPanel(reviewTitle, reviewSubtitle, 'success')}

        {renderNeedsFixingSection()}

        {result.priorRenters.length > 0 ? (
          <Pressable
            onPress={() => setPriorRentersExpanded((prev) => !prev)}
            style={styles.reviewRow}
          >
            <View style={styles.reviewRowHeader}>
              <Text type="bodyRegular" style={{ flex: 1 }}>
                Returning Eon Customer ({result.priorRenters.length})
              </Text>

              <Icon
                type="naked"
                icon={priorRentersExpanded ? <ChevronUp /> : <ChevronDown />}
                fillColor={theme.colors.icon.color}
                width={12}
                height={12}
              />
            </View>

            {priorRentersExpanded ? (
              <View style={{ gap: 12, paddingTop: 12 }}>
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  These customers have rented through Eon before. You still earn
                  70% on these customers even when they book through your own
                  link.
                </Text>

                {result.priorRenters.map((renter) => (
                  <View key={renter.email} style={styles.priorRenterRow}>
                    <Text type="bodyRegular" style={{ flex: 1 }}>
                      {renter.firstName} {renter.lastName}
                    </Text>

                    <Text
                      type="caption1"
                      style={{
                        color: theme.colors.text.secondary,
                        flex: 1,
                        textAlign: 'right',
                      }}
                    >
                      {renter.email}
                    </Text>
                  </View>
                ))}
              </View>
            ) : null}
          </Pressable>
        ) : null}
      </View>
    );
  };

  const renderBody = () => {
    if (step === 'success' && result) {
      return (
        <>
          {renderTemplateCard()}

          {renderSuccessPanel(
            `Import of ${file?.name ?? 'customers.csv'}`,
            getSuccessSubtitle(result)
          )}

          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {CUSTOMER_IMPORT_FOOTER}
          </Text>
        </>
      );
    }

    if (step === 'review') {
      return (
        <>
          {renderTemplateCard()}

          {renderReviewSections()}

          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {CUSTOMER_IMPORT_FOOTER}
          </Text>
        </>
      );
    }

    if (step === 'error') {
      return (
        <>
          {renderTemplateCard()}

          <Alert
            type={AlertType.ERROR}
            title={FILE_IMPORT_ERROR_TITLE}
            body={fileError ?? FILE_IMPORT_ERROR_BODY}
          />

          {renderDropZone()}

          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {CUSTOMER_IMPORT_FOOTER}
          </Text>
        </>
      );
    }

    return (
      <>
        {renderTemplateCard()}

        {renderDropZone()}

        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          {CUSTOMER_IMPORT_FOOTER}
        </Text>
      </>
    );
  };

  return (
    <Modal
      animationIn="fadeIn"
      animationInTiming={200}
      animationOut="fadeOut"
      animationOutTiming={200}
      isVisible={isVisible}
      hasBackdrop
      onBackdropPress={handleClose}
      style={styles.modal}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={{ gap: 8, flex: 1 }}>
            <Text type="title2">Import Customer</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Upload a CSV to add multiple customers at once
            </Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={handleClose}
          />
        </View>

        <ScrollView
          style={styles.bodyScroll}
          contentContainerStyle={styles.body}
          keyboardShouldPersistTaps="handled"
        >
          {renderBody()}
        </ScrollView>

        {step === 'error' ||
        (step === 'review' && result && isTotalImportFailure(result)) ? (
          <View style={styles.footer}>
            <Button
              text="Try Again"
              type="ghost"
              onPress={resetToUpload}
              style={{ width: 'auto' }}
            />
          </View>
        ) : null}
      </View>
    </Modal>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  modal: {
    margin: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: { xs: '95%', md: 568 },
    height: { xs: '90%', md: 680 },
    maxHeight: '90%',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.dialog.borderColor,
    backgroundColor: theme.colors.dialog.backgroundColor,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    paddingTop: 24,
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  bodyScroll: {
    flex: 1,
    minHeight: 0,
  },
  body: {
    paddingTop: 24,
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 16,
  },
  templateCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.background_3,
    backgroundColor: theme.colors.background_3,
  },
  templateDownloadIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropZone: {
    minHeight: 200,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: theme.colors.card.hover.borderColor,
    backgroundColor: theme.colors.dialog.backgroundColor,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 17,
  },
  dropZoneActive: {
    borderColor: theme.colors.icon.brandColor,
  },
  dropZonePressable: {
    flex: 1,
    width: '100%',
    minHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  successPanel: {
    minHeight: 200,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 17,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.success_15,
    backgroundColor: theme.colors.success_15,
  },
  successIconCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.success_15,
    borderWidth: 1,
    borderColor: theme.colors.success_15,
  },
  reviewPanelWarning: {
    minHeight: 200,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 17,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.alert.error.border,
    backgroundColor: theme.colors.alert.error.backgroundColor,
  },
  reviewPanelSuccess: {
    minHeight: 200,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 17,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.success_15,
    backgroundColor: theme.colors.success_15,
  },
  reviewSummaryIconCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.success_15,
    borderWidth: 1,
    borderColor: theme.colors.success_15,
  },
  reviewRow: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.background_3,
    backgroundColor: theme.colors.background_3,
  },
  reviewRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  needsFixingExpandedContent: {
    gap: 12,
    paddingTop: 12,
  },
  needsFixingContainer: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    backgroundColor: theme.colors.dialog.backgroundColor,
  },
  errorReportSection: {
    gap: 4,
  },
  errorList: {
    maxHeight: 160,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingVertical: 6,
  },
  errorRowBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: theme.colors.error_15,
    borderWidth: 1,
    borderColor: theme.colors.error_15,
  },
  errorReportLink: {
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },
  priorRenterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
}));
